import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { trackClick } from '../utils/analytics';

/**
 * First run, two steps: pick an industry, pick a keyword, see real results.
 *
 * The point is to show the product working before asking for anything else —
 * so the second step ends in actual technologies from the corpus, not a
 * "thanks, you're all set" screen. The rest of the profile is collected later
 * from My Profile.
 *
 * Which search runs depends on how the keyword was supplied, per the product
 * spec: a suggested chip is a known taxonomy phrase and goes to the cheap SQL
 * search; anything typed by hand goes to hybrid, which can actually reason
 * about a phrase we have never seen.
 */

interface OnboardingModalLegacyProps {
  industries: string[];
  /** `intent` 'trial' means the person asked for the trial and App has to
   *  finish it: it opens the trial details sheet (name, organization,
   *  designation — no card) over the first screen, via startTrialInPlace. */
  onExplore: (industry: string, keyword: string, intent?: 'trial') => void;
  onSkip: () => void;
}

interface PreviewCard {
  id: string;
  title: string;
  trl?: number | string | null;
  summary: string;
}

const OnboardingModalLegacy: React.FC<OnboardingModalLegacyProps> = ({ industries, onExplore, onSkip }) => {
  const userId = apiService.getStoredUserId() || '';
  const [industry, setIndustry] = useState('');
  const [chips, setChips] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [typed, setTyped] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PreviewCard[] | null>(null);
  const [total, setTotal] = useState(0);
  const [saved, setSaved] = useState<string[]>([]);
  // Whether this account can start a trial, and by which route. Asked once,
  // from the server, so the button reflects what will actually happen. The
  // trial is card-free (the details sheet, then access); the server no
  // longer returns `mode` 'mandate' — the card-first trial was retired.
  const [trial, setTrial] = useState<{ available: boolean; days: number;
    mode?: 'mandate' | 'card' | 'none' } | null>(null);
  const [trialBusy, setTrialBusy] = useState(false);
  useEffect(() => {
    const uid = apiService.getStoredUserId?.();
    if (!uid) return;
    let alive = true;
    (async () => {
      const r = await apiService.getBillingSummary(uid);
      if (alive && r.success && r.data?.success) setTrial(r.data.trial || null);
    })();
    return () => { alive = false; };
  }, []);
  const [error, setError] = useState('');

  // Step 2 unlocks as soon as an industry is chosen.
  useEffect(() => {
    if (!industry) { setChips([]); return; }
    let cancelled = false;
    apiService.getOnboardingKeywords(industry).then(res => {
      if (cancelled) return;
      setChips(res.success && res.data?.success ? res.data.keywords || [] : []);
    });
    apiService.saveOnboarding(userId, { industry });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry]);

  const runSearch = async (term: string, fromChip: boolean) => {
    // Both the suggested chips and the free-text box land here, so this is the
    // one place that sees every keyword choice. `source` is what separates
    // "picked what we offered" from "told us something we didn't suggest" —
    // the second is the interesting one for growing the chip taxonomy.
    trackClick('onboarding_select_keyword', {
      industry, keyword: term, source: fromChip ? 'chip' : 'typed',
    });
    setKeyword(term);
    setLoading(true);
    setError('');
    const backend = fromChip ? 'native' : 'hybrid';
    const res = await apiService.directSearch(backend, {
      query: term, page: 0, hits_per_page: 10,
    });
    setLoading(false);
    if (!res.success || !res.data) {
      setError(res.error || 'Could not fetch results — try another keyword.');
      return;
    }
    const rows = (res.data.results || []).slice(0, 2).map((r: any) => {
      const m = r.metadata || {};
      return {
        id: m.utech_technology_id || m.id,
        title: m.utech_title || m.title || 'Untitled',
        trl: m.calculated_trl,
        // No university on these cards by design — the first thing a new user
        // sees should be the technology, not who owns it.
        summary: m.short_summary || (m.utech_summary || '').split('. ')[0] || '',
      };
    });
    setResults(rows);
    // The badge used to show the keyword's corpus-wide total, which read as
    // "27,688 relevant tech" no matter which industry you picked. The response
    // already carries per-industry facet counts, so use the bucket for the
    // chosen industry and fall back to the total only if it isn't there.
    const cats = res.data.facet_counts?.major_categories
              || res.data.facet_counts?.major_industry_category || {};
    const key = Object.keys(cats).find(
      k => k.toLowerCase() === industry.toLowerCase());
    const scoped = key ? cats[key] : undefined;
    setTotal(typeof scoped === 'number'
      ? scoped
      : (res.data.total_uncapped ?? res.data.total ?? rows.length));
    apiService.saveOnboarding(userId, { industry, keyword: term });
  };

  const step = results ? 2 : 1;

  // Changing the industry must invalidate what is on screen — otherwise the
  // previous industry's preview cards sit under the new industry's label.
  const changeIndustry = (next: string) => {
    trackClick('onboarding_select_industry', { industry: next });
    setIndustry(next);
    setResults(null);
    setKeyword('');
    setTyped('');
    setTotal(0);
    setError('');
  };

  // The bookmark used to be React state only — nothing was ever persisted, so
  // a card saved during onboarding never appeared under the user's profile.
  // Same call the rest of the app makes (see App.tsx onBookmarkToggle).
  const toggleSave = async (id: string) => {
    const next = !saved.includes(id);
    trackClick('onboarding_click_bookmark', { id, saved: next });
    if (!userId || !id) return;
    // Optimistic, reverted if the write fails, so the icon never lies.
    setSaved(sv => next ? [...sv, id] : sv.filter(x => x !== id));
    const res = await apiService.saveCard({
      asset_type: 'technology',
      asset_id: String(id),
      bookmarked: next,
      liked: null,
      linked_chat_id: null,
    }, userId);
    if (!res.success) {
      setSaved(sv => next ? sv.filter(x => x !== id) : [...sv, id]);
      setError('Could not save that card — please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        {/* Two-segment progress rail */}
        <div className="flex gap-2 mb-4">
          <span className="h-1 flex-1 rounded-full bg-primary-500" />
          <span className={`h-1 flex-1 rounded-full ${step === 2 ? 'bg-primary-500' : 'bg-primary-100'}`} />
        </div>
        <div className="text-[11px] font-bold tracking-widest text-primary-700 uppercase mb-1">
          Step {step} of 2
        </div>

        <h2 className="text-xl font-bold text-grayscale-1300 font-grotesk">
          What field are you working in?
        </h2>
        <p className="text-sm text-grayscale-1200 mt-1 mb-4">
          This tunes every match we show you — nothing else to fill in yet.
        </p>

        {/* Industry and keyword stay editable all the way to Explore Cosmos. */}
        <label className="text-xs text-grayscale-1100">Industry</label>
        <select value={industry}
          onChange={e => changeIndustry(e.target.value)}
          className="h-11 px-2.5 mt-1 mb-4 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-full">
          <option value="">Select an industry…</option>
          {industries.map(i => <option key={i} value={i}>{i}</option>)}
        </select>

        {industry && (
          <>
            <label className="text-xs text-grayscale-1100">
              Keyword — pick one or type your own
            </label>
            <input
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && typed.trim()) runSearch(typed.trim(), false);
              }}
              placeholder="Search or type a keyword…"
              className="h-11 px-2.5 mt-1 mb-3 rounded-md border-primary-300 border-[0.5px] bg-white text-sm w-full" />
            <div className="grid grid-cols-3 gap-2 mb-2">
              {chips.map(c => (
                <button key={c} onClick={() => runSearch(c, true)} disabled={loading}
                  className={`px-3 py-2.5 rounded-2xl border text-xs disabled:opacity-50 ${
                    c === keyword
                      ? 'border-primary-500 bg-primary-100 text-primary-700'
                      : 'border-grayscale-300 text-grayscale-1300 hover:border-primary-400 hover:bg-primary-100/30'}`}>
                  {c}
                </button>
              ))}
            </div>
          </>
        )}

        {loading && <div className="text-sm text-grayscale-1100 py-2">Searching Cosmos…</div>}
        {error && <div className="text-sm text-red-600 py-1">{error}</div>}

        {results && !loading && (
          <>
            <div className="flex items-center gap-2 mb-3 mt-1">
              <span className="px-2 py-0.5 rounded-full bg-primary-500 text-grayscale-100 text-xs font-bold">
                {total.toLocaleString()}
              </span>
              <span className="text-sm font-medium text-grayscale-1300">
                Relevant tech in {industry}
              </span>
            </div>

            <div className="flex flex-col gap-3 mb-4">
              {results.map(r => (
                <div key={r.id} className="rounded-xl border border-grayscale-300 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-bold text-grayscale-1300 leading-snug">{r.title}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      {r.trl ? <span className="text-[11px] text-grayscale-1100">TRL {r.trl}</span> : null}
                      <button aria-label="Save" onClick={() => toggleSave(r.id)}
                        className={saved.includes(r.id) ? 'text-primary-600' : 'text-grayscale-400 hover:text-primary-500'}>
                        <svg className="w-4 h-4" viewBox="0 0 24 24"
                             fill={saved.includes(r.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                          <path d="M6 4h12v16l-6-4-6 4V4z" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </span>
                  </div>
                  {r.summary && (
                    <p className="text-xs text-grayscale-1200 mt-1.5 leading-relaxed">{r.summary}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Two ways out of onboarding: take the trial, or carry on with
                the preview cards above. The trial is only offered when the
                server says this account can actually start one, so nobody is
                shown a door that will not open. */}
            <div className="flex flex-col gap-2">
              {trial?.available && (
                <button
                  disabled={trialBusy}
                  onClick={() => {
                    trackClick('onboarding_click_start_trial', {
                      industry, keyword, mode: trial.mode || 'card',
                    });
                    setTrialBusy(true);
                    apiService.saveOnboarding(userId, { industry, keyword, completed: true });
                    // App opens the trial details sheet, in place — the same
                    // starter the paywall uses (startTrialInPlace), so there
                    // is one form and one submit path, not a copy here to
                    // keep in step. No card path from here.
                    onExplore(industry, keyword, 'trial');
                  }}
                  className="w-full py-3 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100 disabled:opacity-50">
                  {trialBusy ? 'Starting…' : `Start ${trial.days}-day free trial`}
                </button>
              )}
              {trial?.available && (
                <span className="text-center text-[11px] text-grayscale-1100 -mt-1">
                  No card required.
                </span>
              )}
              <button
                onClick={() => {
                  trackClick('onboarding_click_explore_cosmos', {
                    industry, keyword, results_total: total, saved_count: saved.length,
                  });
                  apiService.saveOnboarding(userId, { industry, keyword, completed: true });
                  onExplore(industry, keyword);
                }}
                className={`w-full py-3 rounded-full font-grotesk text-sm font-medium ${
                  trial?.available
                    ? 'border border-primary-500 text-primary-700'
                    : 'bg-primary-500 text-grayscale-100'}`}>
                {trial?.available ? 'See the preview cards instead' : 'Explore Cosmos'}
              </button>
            </div>
          </>
        )}

        {!results && (
          <button onClick={() => {
              trackClick('onboarding_click_skip', { step, industry: industry || null });
              apiService.saveOnboarding(userId, { skipped: true });
              onSkip();
            }}
            className="w-full text-sm text-grayscale-1200 hover:text-grayscale-1300 py-2 mt-1">
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardingModalLegacy;
